import { CommonModule } from '@angular/common';
import { Component, Input, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { CustomDateAdapter } from '../../../../../shared/services/custom-date-adapter';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EmptyListComponent } from '../../../../../shared/components/empty-list/empty-list.component';
import { FooterActionComponent } from '../../../../../shared/components/footer-action/footer-action.component';
import { HeaderComponent } from '../../../../../shared/components/header/header.component';
import { FuncionalidadeEnum } from '../../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../../shared/enums/usuario/modulo.enum';
import { OrigemConsultaControleEstoqueEnum } from '../../../../../shared/enums/produto/origem-consulta-controle-estoque.enum';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { MaskService } from '../../../../../shared/services/mask.service';
import { ControleEstoqueService } from '../../../../../shared/services/apis/produto/controle-estoque.service';
import { LookupService } from '../../../../../shared/services/apis/produto/produto-lookup.service';
import { ControleEstoqueListaRequest } from '../../../../../shared/services/requests/produto/controle-estoque-lista.request';
import { CodigoCompostoResolvidoStruct } from '../../../../../shared/services/structs/produto/codigo-composto-resolvido.struct';
import { ControleEstoqueListStruct } from '../../../../../shared/services/structs/produto/controle-estoque-list.struct';
import { DigitacaoProduto, InventarioDigitacaoHost } from '../../inventario-digitacao.models';
import { StatusInventarioEnum } from '../../../../../shared/enums/suprimentos/status-inventario.enum';
import { InventarioItemSheetComponent, ItemSheetLocalOpcao, ItemSheetPrefill, ItemSheetResultado } from '../inventario-item-sheet/inventario-item-sheet.component';
import { InventarioScannerMobileComponent } from '../../../../../shared/components/inventario-scanner-mobile/inventario-scanner-mobile.component';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';

type FiltroCard = 'todos' | 'pendentes' | 'lancados' | 'divergentes' | 'loteSerie';

interface CardItem {
  produto: DigitacaoProduto;
  totalContado: number;
  algumContado: boolean;
  todosContados: boolean;
  ajusteTotal: number;
  temLoteSerie: boolean;
  rastreabilidadePendente: boolean;
  rastreabilidadeConfirmada: boolean;
}

// Experiência mobile da digitação de inventário: cabeçalho compacto, pesquisa + leitor de
// câmera, cards dos itens e painel inferior para lançar contagem. O estado mora no host
// (componente desktop); aqui só lemos/disparamos ações.
@Component({
  selector: 'app-inventario-digitacao-mobile',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    HeaderComponent,
    FooterActionComponent,
    EmptyListComponent,
    InventarioScannerMobileComponent,
    InventarioItemSheetComponent,
    AbntPipe,
  ],
  templateUrl: './inventario-digitacao-mobile.component.html',
  styleUrl: './inventario-digitacao-mobile.component.css',
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    { provide: DateAdapter, useClass: CustomDateAdapter },
  ],
})
export class InventarioDigitacaoMobileComponent {
  @Input() host!: InventarioDigitacaoHost;

  @ViewChild(InventarioScannerMobileComponent) scannerRef?: InventarioScannerMobileComponent;

  public moduloEnum = ModuloEnum.Suprimentos;
  public funcionalidadeEnum = FuncionalidadeEnum.Suprimentos_InventarioEstoque;
  public readonly hoje: Date = new Date();
  public _StatusInventarioEnum = StatusInventarioEnum;

  public termoBusca = '';
  public buscando = false;
  public scannerAberto = false;

  // seleção de múltiplos resultados
  public resultados: ControleEstoqueListStruct[] = [];

  // painel inferior
  public sheetAberto = false;
  public sheetProduto?: ControleEstoqueListStruct;
  public sheetLocais: ItemSheetLocalOpcao[] = [];
  public sheetPrefill?: ItemSheetPrefill;
  public sheetCarregandoProduto = false;
  public sheetPermiteSelecionarProduto = false;

  private codigoCompostoResolvido?: CodigoCompostoResolvidoStruct;
  private produtoRequestId = 0;

  public filtros: { chave: FiltroCard; rotulo: string }[] = [
    { chave: 'todos', rotulo: 'Todos' },
    { chave: 'pendentes', rotulo: 'Pendentes' },
    { chave: 'lancados', rotulo: 'Lançados' },
    { chave: 'divergentes', rotulo: 'Divergentes' },
    { chave: 'loteSerie', rotulo: 'Lote/Série' },
  ];
  public filtroAtivo: FiltroCard = 'todos';

  constructor(
    private controleEstoqueService: ControleEstoqueService,
    private alertService: AlertService,
    private lookupService: LookupService
  ) {}

  // ── Cards ──────────────────────────────────────────────────────────────────
  public get cards(): CardItem[] {
    return (this.host?.itens ?? []).map(produto => {
      const locais = produto.locais;
      const temLoteSerie = produto.isControlaLotes || produto.isControlaSeries;
      const contados = locais.filter(local =>
        local.quantidadeInventariada != null
        && (!temLoteSerie || !!local.confirmadoRastreabilidade));
      const totalContado = contados.reduce((s, l) => s + (l.quantidadeInventariada ?? 0), 0);
      const ajusteTotal = contados.reduce((s, l) => s + ((l.quantidadeInventariada ?? 0) - l.saldoInicial), 0);
      const rastreabilidadeConfirmada = temLoteSerie
        && locais.length > 0
        && locais.every(local => local.confirmadoRastreabilidade);
      return {
        produto,
        totalContado,
        algumContado: contados.length > 0,
        todosContados: locais.length > 0 && contados.length === locais.length,
        ajusteTotal,
        temLoteSerie,
        rastreabilidadePendente: temLoteSerie && !rastreabilidadeConfirmada,
        rastreabilidadeConfirmada,
      };
    });
  }

  public get cardsFiltrados(): CardItem[] {
    const cards = this.cards;
    switch (this.filtroAtivo) {
      case 'pendentes': return cards.filter(c => !c.todosContados);
      case 'lancados': return cards.filter(c => c.todosContados);
      case 'divergentes': return cards.filter(c => c.algumContado && c.ajusteTotal !== 0);
      case 'loteSerie': return cards.filter(c => c.temLoteSerie);
      default: return cards;
    }
  }

  public contar(filtro: FiltroCard): number {
    const cards = this.cards;
    switch (filtro) {
      case 'pendentes': return cards.filter(c => !c.todosContados).length;
      case 'lancados': return cards.filter(c => c.todosContados).length;
      case 'divergentes': return cards.filter(c => c.algumContado && c.ajusteTotal !== 0).length;
      case 'loteSerie': return cards.filter(c => c.temLoteSerie).length;
      default: return cards.length;
    }
  }

  // ── Busca / leitura ──────────────────────────────────────────────────────────
  public buscarManual(): void {
    if (!this.termoBusca.trim()) return;
    if (!this.host.validarCabecalhoObrigatorio()) return;
    this.buscar(this.termoBusca, true);
  }

  public onCodigoLido(codigo: string): void {
    this.scannerRef?.pausar();
    this.buscar(codigo, false, true);
  }

  private buscar(texto: string, permitirFallbackLista: boolean, viaScanner = false): void {
    const termo = (texto ?? '').trim();
    if (!termo) return;

    this.buscando = true;
    this.resultados = [];

    this.controleEstoqueService.resolverCodigoInventario({ codigo: termo }).subscribe({
      next: res => {
        if (res.isError) {
          this.buscando = false;
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
          this.retomarScanner();
          return;
        }

        if (res.produto) {
          this.buscando = false;
          this.codigoCompostoResolvido = res.codigoComposto ?? undefined;
          this.selecionarProduto(res.produto, viaScanner);
          return;
        }

        if (permitirFallbackLista) {
          this.buscarListaPorTexto(termo);
          return;
        }

        this.buscando = false;
        this.alertService.show('Atenção', `Nenhum produto encontrado para o código ${termo}`, AlertType.warning);
        this.retomarScanner();
      },
      error: err => {
        this.buscando = false;
        this.alertService.show('Erro', err?.message || err, AlertType.error);
        this.retomarScanner();
      },
    });
  }

  private buscarListaPorTexto(termo: string): void {
    const req = new ControleEstoqueListaRequest();
    req.origem = OrigemConsultaControleEstoqueEnum.InventarioEstoque;
    req.searchText = termo;

    this.controleEstoqueService.getAll(req).subscribe({
      next: res => {
        this.buscando = false;
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
          this.retomarScanner();
          return;
        }

        const lista = res.list || [];
        if (lista.length === 0) {
          this.alertService.show('Atenção', `Nenhum produto encontrado para o código ${termo}`, AlertType.warning);
          this.retomarScanner();
          return;
        }

        if (lista.length === 1) {
          this.selecionarProduto(lista[0]);
        } else {
          this.resultados = lista;
        }
      },
      error: err => {
        this.buscando = false;
        this.alertService.show('Erro', err?.message || err, AlertType.error);
        this.retomarScanner();
      },
    });
  }

  public selecionarProduto(p: ControleEstoqueListStruct, viaScanner = false): void {
    this.resultados = [];
    const idsLoc: number[] = this.host.formInventario.get('idsLocalizacoes')!.value ?? [];

    const existente = this.host.encontrarProduto(p.idProduto);
    if (existente) {
      // Leitura repetida do scanner para um item já contado: incrementa direto (sem abrir o
      // painel) quando não há ambiguidade — um único local ativo e sem lote/série (que exigem
      // escolher explicitamente qual lote/série está sendo lido).
      if (viaScanner && !p.isControlaLotes && !p.isControlaSeries && existente.locais.length === 1) {
        this.incrementarContagemExistente(p, existente);
        return;
      }
      this.abrirSheet(p, existente, false);
      return;
    }

    if (idsLoc.length === 0) {
      this.alertService.show('Atenção', 'Selecione ao menos uma localização no cabeçalho antes de adicionar produtos.', AlertType.warning);
      this.retomarScanner();
      return;
    }

    this.abrirSheet(p, undefined, false);
  }

  private incrementarContagemExistente(p: ControleEstoqueListStruct, existente: DigitacaoProduto): void {
    const local = existente.locais[0];
    const novaQuantidade = (local.quantidadeInventariada ?? 0) + 1;
    this.host.registrarContagem(existente.idProduto, local.idLocalizacaoEstoque, novaQuantidade);
    this.alertService.show('', `${p.descricao}: ${novaQuantidade} ${p.unidade ?? ''}`.trim(), AlertType.success);
    this.retomarScanner();
  }

  public async abrirAdicionarProduto(): Promise<void> {
    if (!this.host.validarCabecalhoObrigatorio()) return;

    const podeContinuar = await this.host.sugerirSalvarRascunhoNovoSeNecessario();
    if (!podeContinuar) return;

    this.codigoCompostoResolvido = undefined;
    this.produtoRequestId++;
    this.sheetProduto = undefined;
    this.sheetLocais = [];
    this.sheetPrefill = undefined;
    this.sheetCarregandoProduto = false;
    this.sheetPermiteSelecionarProduto = true;
    this.sheetAberto = true;
  }

  public onSheetProdutoSelecionado(selecao: any): void {
    const idProduto = typeof selecao === 'number' ? selecao : selecao?.idProduto;
    if (!idProduto) return;

    const requestId = ++this.produtoRequestId;
    const existente = this.host.encontrarProduto(idProduto);
    if (existente) {
      this.abrirSheet(this.criarProdutoDoInventario(existente), existente, true);
      return;
    }

    // Campos descritivos já vêm completos no objeto do autocomplete (Lookup/listProdutoSelect) —
    // só o saldo por localização precisa de uma consulta à parte (feita depois de abrir o painel).
    const abrirComDados = (dados: any): void => {
      if (requestId !== this.produtoRequestId) return;

      const produto = new ControleEstoqueListStruct();
      produto.idProduto = dados.idProduto;
      produto.descricao = dados.descricao;
      produto.codigoSku = dados.codigoSku ?? undefined;
      produto.gtinEan = dados.gtinEan ?? undefined;
      produto.unidade = dados.unidade ?? 'UN';
      produto.custoMedio = dados.custoMedio ?? 0;
      produto.isControlaLotes = dados.isControlaLotes ?? false;
      produto.isControlaSeries = dados.isControlaSeries ?? false;

      this.sheetCarregandoProduto = false;
      this.abrirSheet(produto, undefined, true);

      const idsLoc: number[] = this.host.formInventario.get('idsLocalizacoes')!.value ?? [];
      this.lookupService.getProdutoSaldoLocal(idProduto, idsLoc).subscribe({
        next: resp => {
          if (requestId !== this.produtoRequestId || !this.sheetAberto || resp?.isError) return;
          produto.locais = (resp.list ?? []).map(l => ({
            idLocalizacaoEstoque: l.idLocalizacaoEstoque,
            saldoFisico: l.saldoFisico,
            saldoReservado: l.saldoReservado,
            saldoDisponivel: l.saldoDisponivel,
          }));
          this.sheetLocais = this.criarLocaisTemporarios(produto);
        },
      });
    };

    if (selecao && typeof selecao === 'object' && selecao.idProduto) {
      abrirComDados(selecao);
      return;
    }

    // Fallback defensivo: só o id foi passado (sem o objeto do autocomplete) — busca os campos
    // descritivos no mesmo lookup livre usado pelo autocomplete, sem exigir permissão de Cadastros.
    this.sheetProduto = undefined;
    this.sheetLocais = [];
    this.sheetPrefill = undefined;
    this.sheetCarregandoProduto = true;
    this.lookupService.getAllSelectByIds([idProduto]).subscribe({
      next: resp => {
        if (requestId !== this.produtoRequestId || !this.sheetAberto) return;
        const dados = resp?.list?.[0];
        if (resp?.isError || !dados) {
          this.sheetCarregandoProduto = false;
          this.alertService.show('Erro', 'Não foi possível carregar o produto.', AlertType.error);
          return;
        }
        abrirComDados(dados);
      },
      error: () => {
        if (requestId !== this.produtoRequestId) return;
        this.sheetCarregandoProduto = false;
        this.alertService.show('Erro', 'Não foi possível carregar o produto.', AlertType.error);
      },
    });
  }

  private abrirSheet(p: ControleEstoqueListStruct, prod?: DigitacaoProduto, permiteSelecionarProduto = false): void {
    this.scannerRef?.pausar();
    this.sheetProduto = p;
    this.sheetLocais = prod
      ? prod.locais.map(l => ({
          idLocalizacaoEstoque: l.idLocalizacaoEstoque,
          idInventarioItem: l.idInventarioItem,
          nome: l.nomeLocalizacao,
          saldoInicial: l.saldoInicial,
          quantidadeInventariada: l.quantidadeInventariada,
          lotes: (l.lotes ?? []).map(x => ({ ...x })),
          series: (l.series ?? []).map(x => ({ ...x })),
          confirmadoRastreabilidade: l.confirmadoRastreabilidade,
        }))
      : this.criarLocaisTemporarios(p);
    this.sheetPrefill = this.codigoCompostoResolvido?.codigo ? { codigoLoteSerie: this.codigoCompostoResolvido.codigo } : undefined;
    this.sheetCarregandoProduto = false;
    this.sheetPermiteSelecionarProduto = permiteSelecionarProduto;
    this.sheetAberto = true;
    this.termoBusca = '';
  }

  private criarLocaisTemporarios(p: ControleEstoqueListStruct): ItemSheetLocalOpcao[] {
    const idsLoc: number[] = this.host.formInventario.get('idsLocalizacoes')!.value ?? [];
    return idsLoc.map(idLocalizacaoEstoque => {
      const localProduto = (p.locais ?? []).find(local => local.idLocalizacaoEstoque === idLocalizacaoEstoque);
      const localSelecionado = this.host.listaLocalizacoes.find(local => local.id === idLocalizacaoEstoque);
      return {
        idLocalizacaoEstoque,
        nome: localSelecionado?.descricao ?? `Localização ${idLocalizacaoEstoque}`,
        saldoInicial: localProduto?.saldoFisico ?? 0,
        quantidadeInventariada: null,
        confirmadoRastreabilidade: false,
      };
    });
  }

  public async onSheetConfirmar(result: ItemSheetResultado): Promise<void> {
    const p = this.sheetProduto;
    if (!p) return;

    let produtoInventario = this.host.encontrarProduto(p.idProduto);
    if (!produtoInventario) {
      this.host.incluirProdutoDaBusca(p);
      produtoInventario = this.host.encontrarProduto(p.idProduto);
    }
    if (!produtoInventario) {
      this.alertService.show('Erro', 'Não foi possível adicionar o produto ao inventário.', AlertType.error);
      return;
    }

    if (result.lotes) {
      this.host.aplicarLotesNoLocal(p.idProduto, result.idLocalizacaoEstoque, result.lotes);
    } else if (result.series) {
      this.host.aplicarSeriesNoLocal(p.idProduto, result.idLocalizacaoEstoque, result.series);
    } else {
      this.host.registrarContagem(p.idProduto, result.idLocalizacaoEstoque, result.quantidade);
    }
    this.host.rebuildGrid();

    if (this.host.isEdicao || this.host.idInventario > 0) {
      const salvou = await this.host.salvarRascunhoAutomatico();
      this.alertService.show(
        salvou ? 'Sucesso' : 'Atenção',
        salvou ? 'Contagem salva.' : 'Contagem registrada, mas não foi possível salvar automaticamente.',
        salvou ? AlertType.success : AlertType.warning
      );
    } else {
      this.alertService.show('Sucesso', 'Contagem registrada.', AlertType.success);
      await this.host.sugerirSalvarRascunhoNovoSeNecessario();
    }

    this.fecharSheet();
  }

  public onSheetLimpar(): void {
    // mantém o painel aberto; apenas limpa as entradas (tratado no próprio sheet).
  }

  public fecharSheet(): void {
    this.produtoRequestId++;
    this.sheetAberto = false;
    this.sheetProduto = undefined;
    this.sheetLocais = [];
    this.sheetPrefill = undefined;
    this.sheetCarregandoProduto = false;
    this.sheetPermiteSelecionarProduto = false;
    this.codigoCompostoResolvido = undefined;
    this.retomarScanner();
  }

  // Abre o painel para um card já existente (toque no card).
  public abrirCard(card: CardItem): void {
    this.codigoCompostoResolvido = undefined;
    this.abrirSheet(this.criarProdutoDoInventario(card.produto), card.produto, false);
  }

  private criarProdutoDoInventario(produtoInventario: DigitacaoProduto): ControleEstoqueListStruct {
    const produto = new ControleEstoqueListStruct();
    produto.idProduto = produtoInventario.idProduto;
    produto.descricao = produtoInventario.nome;
    produto.codigoSku = produtoInventario.sku;
    produto.unidade = produtoInventario.unidade;
    produto.custoMedio = produtoInventario.custoMedio;
    produto.isControlaLotes = produtoInventario.isControlaLotes;
    produto.isControlaSeries = produtoInventario.isControlaSeries;
    return produto;
  }

  // ── Scanner ────────────────────────────────────────────────────────────────
  public abrirLeitor(): void {
    if (!this.host.validarCabecalhoObrigatorio()) return;
    this.scannerAberto = true;
  }

  public fecharLeitor(): void {
    this.scannerAberto = false;
  }

  private retomarScanner(): void {
    if (this.scannerAberto) {
      setTimeout(() => this.scannerRef?.retomar(), 0);
    }
  }

  // ── Footer ───────────────────────────────────────────────────────────────────
  public get salvarDesabilitado(): boolean {
    return this.host.isSalvandoRascunho || this.host.isConcluindo;
  }

  public get rascunhoDesabilitado(): boolean {
    return this.host.isSalvandoRascunho || this.host.isConcluindo;
  }

  // ── Data do inventário (digitação inline DD/MM/AAAA, sem futuro) ──────────────
  public onDateInput(event: Event): void {
    MaskService.onDateInput(event);
  }

  public onDateKeydown(event: KeyboardEvent): void {
    const teclasPermitidas = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter'];
    const isAtalho = (event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase());
    if (teclasPermitidas.includes(event.key) || isAtalho) return;
    if (!/^\d$/.test(event.key)) event.preventDefault();
  }

  public onDataInventarioBlur(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    const data = this.lerDataInput(input);
    const control = this.host.formInventario.get('dataInventario');
    if (data && data > this.hojeNormalizado()) {
      input.value = '';
      control?.setValue(null);
      control?.markAsDirty();
      this.alertService.show('Atenção', 'A data do inventário não pode ser maior que a data atual.', AlertType.warning);
      return;
    }
    control?.setValue(data);
    control?.markAsDirty();
  }

  private lerDataInput(input: HTMLInputElement): Date | null {
    const valor = (input?.value || '').trim();
    if (!valor) return null;
    const apenasNumeros = valor.replace(/\D/g, '').substring(0, 8);
    if (apenasNumeros.length !== 8) {
      input.value = '';
      this.alertService.show('Atenção', 'Informe uma data válida no formato DD/MM/AAAA.', AlertType.warning);
      return null;
    }
    const formatado = `${apenasNumeros.substring(0, 2)}/${apenasNumeros.substring(2, 4)}/${apenasNumeros.substring(4, 8)}`;
    input.value = formatado;
    const data = this.parseDataBr(formatado);
    if (!data) {
      input.value = '';
      this.alertService.show('Atenção', 'Informe uma data válida no formato DD/MM/AAAA.', AlertType.warning);
    }
    return data;
  }

  private parseDataBr(valor: string): Date | null {
    const partes = valor.split('/');
    if (partes.length !== 3) return null;
    const dia = Number(partes[0]);
    const mes = Number(partes[1]);
    const ano = Number(partes[2]);
    if (!Number.isInteger(dia) || !Number.isInteger(mes) || !Number.isInteger(ano)) return null;
    if (ano < 1000 || ano > 9999 || mes < 1 || mes > 12 || dia < 1 || dia > 31) return null;
    const data = new Date(ano, mes - 1, dia);
    data.setHours(0, 0, 0, 0);
    if (data.getFullYear() !== ano || data.getMonth() !== mes - 1 || data.getDate() !== dia) return null;
    return data;
  }

  private hojeNormalizado(): Date {
    const h = new Date();
    h.setHours(0, 0, 0, 0);
    return h;
  }
}
